import { parseISO } from 'date-fns';
import isNil from 'lodash/isNil';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadPDF, DownloadPDFProps } from '~/components/invoices/PreviewInvoice/DownloadPDF';
import Modal from '~/components/modals/Modal/Modal';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import Text from '~/components/typographies/Text';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useInvoiceXml from '~/hooks/useInvoiceXml';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useInvoicingSettings } from '~/services/company-settings';
import { useEInvoice, useInvoice } from '~/services/invoicing';
import { useGrossPricesState } from '~/services/items';
import { Document, DocumentStatus } from '~/types/document';
import { assertIsDefined } from '~/types/typeGuards';
import { counterpartName } from '~/utils/counterparts';
import { documentPreviewType } from '~/utils/document';
import { eInvoiceToForm } from '~/utils/einvoice';
import { documentPreviewAdditionalData, useInvoiceDownloadName } from '~/utils/invoicing';
import { PrintButton, PrintButtonProps } from '~/views/invoices/preview/PrintButton';
import AssosoftwarePreviewContent from './AssosoftwarePreviewContent';
import styles from './DocumentPreviewDialog.module.scss';
import SibillPreviewContent from './SibillPreviewContent';

export interface DocumentPreviewDialogProps extends ModalProps {
  document: Document;
}

const PreviewDialogLayout: React.FC<
  PropsWithChildren<
    DocumentPreviewDialogProps & {
      DownloadPDFProps: Omit<DownloadPDFProps, 'children'>;
      PrintPDFProps: PrintButtonProps;
    }
  >
> = ({ document, closeModal, DownloadPDFProps, PrintPDFProps, children }) => {
  const { t } = useTranslation('documents', { keyPrefix: 'preview_dialog' });

  return (
    <Modal isOpen fullWidth maxWidth={1280}>
      <ModalTitle onCloseClick={closeModal} className={styles.title}>
        {t('title')}
      </ModalTitle>
      <ModalContent className={styles.content}>
        <div className={styles.actionsContainer}>
          <PrintButton {...PrintPDFProps}>{t('print')}</PrintButton>
          <div className={styles.actions}>
            <DownloadPDF {...DownloadPDFProps}>{t('download_PDF', { context: document.status })}</DownloadPDF>
          </div>
        </div>
        {children}
      </ModalContent>
    </Modal>
  );
};

const XmlDocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({ document, closeModal }) => {
  const { isLoading, xmlDocument, loadXml, hasDownloadedAttachment } = useInvoiceXml(document.id);
  const { track } = useAnalytics();
  const { t } = useTranslation('documents', { keyPrefix: 'preview_dialog' });
  const invoiceContentRef = useRef<HTMLDivElement | null>(null);
  const downloadFilename = useInvoiceDownloadName();

  const trackDownload = () => {
    track(AnalyticsEvents.DOCUMENT_COPY_DOWNLOADED, {
      documentType: document.documentType,
      version: 'xmls',
      context: '/preview-dialog',
    });
  };

  const trackPrint = () => {
    track(AnalyticsEvents.DOCUMENT_COPY_PRINTED, {
      documentType: document.documentType,
      version: 'xmls',
      context: '/preview-dialog',
    });
  };

  useEffect(() => {
    loadXml();
  }, [loadXml]);

  useEffect(() => {
    if (hasDownloadedAttachment) {
      closeModal();
    }
  }, [closeModal, hasDownloadedAttachment]);

  if (isLoading || isNil(xmlDocument))
    return (
      <Text variant="body1" weight="regular">
        {t('loading')}
      </Text>
    );
  const filename = downloadFilename({
    counterpart: counterpartName(document.counterpart),
    creationDate: parseISO(document.creationDate),
    documentType: document.documentType,
    number: document.number,
    status: document.status,
  });

  return (
    <PreviewDialogLayout
      closeModal={closeModal}
      document={document}
      DownloadPDFProps={{
        filename,
        element: invoiceContentRef,
        onSuccess: trackDownload,
      }}
      PrintPDFProps={{
        element: invoiceContentRef,
        onSuccess: trackPrint,
      }}
    >
      <AssosoftwarePreviewContent
        ref={invoiceContentRef}
        xmlDocument={xmlDocument}
        className={styles.xmlPreviewContent}
      />
    </PreviewDialogLayout>
  );
};

const SibillDocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({ document: inDocument, closeModal }) => {
  const { data: eInvoice, isLoading: isLoadingEInvoice } = useEInvoice(inDocument.id);
  const { data: document, isLoading: isLoadingDocument } = useInvoice(inDocument.id);

  const { track } = useAnalytics();
  const { t } = useTranslation('documents', { keyPrefix: 'preview_dialog' });
  const invoiceContentRef = useRef<HTMLDivElement | null>(null);
  const invoicingSettings = useInvoicingSettings();
  const activeCompany = useActiveCompany();
  const [useGrossPrices] = useGrossPricesState();

  const downloadFilename = useInvoiceDownloadName();

  const trackDownload = () => {
    assertIsDefined(document, 'document must be defined');

    track(
      document.status === DocumentStatus.Draft
        ? AnalyticsEvents.DOCUMENT_DRAFT_DOWNLOADED
        : AnalyticsEvents.DOCUMENT_COPY_DOWNLOADED,
      { documentType: document.documentType, version: 'sibill', context: '/preview-dialog' },
    );
  };

  const trackPrint = () => {
    assertIsDefined(document, 'document must be defined');
    track(
      document.status === DocumentStatus.Draft
        ? AnalyticsEvents.DOCUMENT_DRAFT_PRINTED
        : AnalyticsEvents.DOCUMENT_COPY_PRINTED,
      { documentType: document.documentType, version: 'sibill', context: '/preview-dialog' },
    );
  };

  if (isLoadingEInvoice || isNil(eInvoice) || isLoadingDocument || isNil(document))
    return (
      <Text variant="body1" weight="regular">
        {t('loading')}
      </Text>
    );

  const additionalPreviewData = documentPreviewAdditionalData(document);

  const values = eInvoiceToForm(
    invoicingSettings,
    activeCompany.fiscalRegime,
    undefined,
    eInvoice,
    inDocument.documentType,
    useGrossPrices,
  );

  const filename = downloadFilename({
    counterpart: counterpartName(document.counterpart),
    creationDate: values.date,
    documentType: document.documentType,
    number: document.number,
    status: document.status,
  });

  return (
    <PreviewDialogLayout
      closeModal={closeModal}
      document={document}
      DownloadPDFProps={{
        filename,
        element: invoiceContentRef,
        onSuccess: trackDownload,
      }}
      PrintPDFProps={{
        element: invoiceContentRef,
        onSuccess: trackPrint,
      }}
    >
      <SibillPreviewContent additionalData={additionalPreviewData} eInvoice={eInvoice} />
      <div className={styles.hidden}>
        <SibillPreviewContent
          additionalData={additionalPreviewData}
          disableTabularNums
          eInvoice={eInvoice}
          showDraft
          ref={invoiceContentRef}
        />
      </div>
    </PreviewDialogLayout>
  );
};

const DocumentPreviewDialog = (props: DocumentPreviewDialogProps) => {
  if (documentPreviewType(props.document) === 'sibill') {
    return <SibillDocumentPreviewDialog {...props} />;
  }

  return <XmlDocumentPreviewDialog {...props} />;
};

export default DocumentPreviewDialog;
